import { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from '../../lib/callable';
import { functions } from '../../firebase';
import { CALLABLE_TIMEOUTS } from '../../constants/firebase';
import PlatformErrorsSection from './PlatformErrorsSection';
import {
  Link2,
  AlertTriangle,
  CircleSlash,
  CircleHelp,
  CheckCircle2,
  Clock,
  Info,
  Search,
} from 'lucide-react';

/**
 * The chain per study — SparkVerify spec §8, build step S3.
 *
 * THIS PAGE COMPUTES NOTHING. §8: "No surface makes claims. Every surface
 * renders stored §5 records and nothing else." The whole computation lives in
 * `functions/src/verification/chainView.ts` and is proven in the suite; this
 * file chooses colours and words for what that module already decided. If a
 * question about the chain can be answered here, it is in the wrong place.
 *
 * WHY NOTHING GOES GREY BELOW A RED LINK. Chain map §1.1 measured the chain as
 * a fan-out, not a line: 45 modules read the fact base directly. So a broken L1
 * does not make the SDS wrong, and a view that greyed it out would be "lying in
 * the safe direction — but lying". Downstream risk shows up as STALENESS, which
 * the records can actually support.
 *
 * Internal and admin-only. No client-facing surface changes.
 */

type DirectionState =
  | 'holds' | 'partial' | 'broken' | 'findings' | 'warn' | 'unverified' | 'unchecked';

interface Acknowledgment {
  adjudication: 'true-finding-recorded' | 'false-positive-recorded' | 'resolved';
  acknowledgedBy: string;
  acknowledgedAt: string;
  note?: string;
}

interface DirectionView {
  state: DirectionState;
  /** A human has adjudicated at least one finding here. Quiet, never hidden. */
  acknowledged?: boolean;
  placedChecks: string[];
  records: Array<{
    checkId: string;
    verdict: string;
    ranAt?: string;
    codeCommit?: string;
    controlProven?: boolean;
    offendingItems?: string[];
    target?: { deliverable?: string; artifactId?: string };
    acknowledgment?: Acknowledgment;
  }>;
  offendingItems: string[];
  deliverables: string[];
  stale: boolean;
  staleKinds: string[];
  staleReason: string | null;
  unprovenControl: boolean;
  qualifier: string | null;
  partialReason: string | null;
  ranAt: string | null;
}

interface LinkView {
  id: string;
  from: string;
  to: string;
  directions: { loss: DirectionView; invention: DirectionView };
}

interface ChainViewPayload {
  projectId: string;
  recordCount: number;
  links: LinkView[];
  summary: {
    total: number; holds: number; partial: number; broken: number;
    findings: number; warn: number; unverified: number; unchecked: number;
    claimHolds: boolean; blockedBy: string[];
  };
}

interface PlatformPayload {
  deployedCommit: string;
  walkthrough: null | {
    runId: string; verdict: string; ranAt: string | null; ageHours: number | null;
    observations: Record<string, number>; offendingItems: string[]; source: string;
  };
  openAlerts: Array<{
    id: string; projectId: string; checkId: string; deliverable: string;
    offendingItems: string[]; ranAt: string | null;
  }>;
}

/**
 * One row per state. `unverified` is deliberately NOT green and NOT red: §5.1a
 * makes a link whose checks were skipped UNVERIFIED rather than holding, and
 * the whole point is that it reads as neither pass nor failure.
 */
const STATE: Record<DirectionState, { label: string; cls: string; Icon: typeof CheckCircle2 }> = {
  holds:      { label: 'Holds',      cls: 'bg-green-50 text-green-800 border-green-200',   Icon: CheckCircle2 },
  partial:    { label: 'Partial',    cls: 'bg-amber-50 text-amber-800 border-amber-200',   Icon: Info },
  broken:     { label: 'Broken',     cls: 'bg-red-50 text-red-800 border-red-200',         Icon: AlertTriangle },
  findings:   { label: 'Findings',   cls: 'bg-sky-50 text-sky-800 border-sky-200',         Icon: Info },
  warn:       { label: 'Warn',       cls: 'bg-orange-50 text-orange-800 border-orange-200',Icon: AlertTriangle },
  unverified: { label: 'Unexamined', cls: 'bg-slate-100 text-slate-700 border-slate-300',  Icon: CircleSlash },
  unchecked:  { label: 'No check',   cls: 'bg-white text-gray-500 border-gray-200',        Icon: CircleHelp },
};

function StateBadge({ state }: { state: DirectionState }) {
  const { label, cls, Icon } = STATE[state];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${cls}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

function DirectionCell({ d, title }: { d: DirectionView; title: string }) {
  return (
    <div className="p-3 border rounded bg-white">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-xs uppercase tracking-wide text-gray-500">{title}</span>
        <div className="flex items-center gap-1">
          {/* Judged, and still shown. A read finding is quiet, not invisible. */}
          {d.acknowledged && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium bg-indigo-50 text-indigo-700 border-indigo-200">
              Judged
            </span>
          )}
          <StateBadge state={d.state} />
        </div>
      </div>

      {/* WHO judged it, HOW, and WHEN — read from the stored acknowledgment. */}
      {d.records.filter((r) => r.acknowledgment).map((r) => (
        <div
          key={`ack-${r.checkId}`}
          className="mt-1 text-xs text-indigo-900 bg-indigo-50 border border-indigo-200 rounded px-2 py-1"
        >
          <strong>{r.checkId} judged:</strong> {r.acknowledgment!.adjudication.replace(/-/g, ' ')} —{' '}
          {r.acknowledgment!.acknowledgedBy} on {r.acknowledgment!.acknowledgedAt.slice(0, 10)}
          {r.acknowledgment!.note ? ` — ${r.acknowledgment!.note}` : ''}
        </div>
      ))}

      {d.placedChecks.length > 0 && (
        <div className="text-xs text-gray-500 mb-1">{d.placedChecks.join(', ')}</div>
      )}
      {d.placedChecks.length === 0 && (
        <div className="text-xs text-gray-400 mb-1 italic">
          the chain map places no check on this direction
        </div>
      )}

      {/* Staleness, per §5.3. A stale green is not green. */}
      {d.stale && (
        <div className="mt-1 flex items-start gap-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          <Clock className="w-3 h-3 mt-0.5 shrink-0" />
          <span>
            <strong>Stale ({d.staleKinds.join(' + ')}).</strong> {d.staleReason}
          </span>
        </div>
      )}

      {/* §5.2's qualifier. Rendered on the PASS, where it changes the reading. */}
      {d.unprovenControl && (
        <div className="mt-1 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1">
          <strong>Unproven control.</strong> {d.qualifier}
        </div>
      )}

      {/* A partial says WHY, from the chain map's own gap row. */}
      {d.partialReason && (
        <details className="mt-1">
          <summary className="text-xs text-amber-800 cursor-pointer">Why partial</summary>
          <p className="text-xs text-gray-600 mt-1 leading-snug">{d.partialReason}</p>
        </details>
      )}

      {/* A FAIL names its offenders and points at the artifact. */}
      {d.offendingItems.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {d.offendingItems.slice(0, 6).map((item, i) => (
            <li key={i} className="text-xs text-gray-700 break-words">— {item}</li>
          ))}
          {d.offendingItems.length > 6 && (
            <li className="text-xs text-gray-500">…and {d.offendingItems.length - 6} more</li>
          )}
        </ul>
      )}
      {d.deliverables.length > 0 && (
        <div className="mt-1 text-xs text-gray-500">in: {d.deliverables.join(', ')}</div>
      )}
      {d.ranAt && <div className="mt-1 text-[11px] text-gray-400">last run {d.ranAt}</div>}
    </div>
  );
}

export default function ChainDashboard() {
  const [projectId, setProjectId] = useState('');
  const [pending, setPending] = useState('');
  const [view, setView] = useState<ChainViewPayload | null>(null);
  const [platform, setPlatform] = useState<PlatformPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    httpsCallable(functions, 'getPlatformVerificationStateV2', { timeout: CALLABLE_TIMEOUTS.SHORT })({})
      .then((r) => setPlatform(r.data as PlatformPayload))
      .catch((e) => setError(String(e?.message ?? e)));
  }, []);

  const load = useCallback(async (id: string) => {
    if (!id.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const r = await httpsCallable(functions, 'getChainViewV2', { timeout: CALLABLE_TIMEOUTS.SHORT })({ projectId: id.trim() });
      setView(r.data as ChainViewPayload);
      setProjectId(id.trim());
    } catch (e: any) {
      setError(String(e?.message ?? e));
      setView(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const counts = useMemo(() => {
    if (!view) return null;
    const s = view.summary;
    return [
      ['Holds', s.holds], ['Partial', s.partial], ['Broken', s.broken],
      ['Findings', s.findings], ['Warn', s.warn],
      ['Unexamined', s.unverified], ['No check', s.unchecked],
    ] as Array<[string, number]>;
  }, [view]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Link2 className="w-5 h-5 text-blue-600" />
          Chain verification
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Every link between the protocol and a generated document, in both directions,
          rendered from stored verification records. Nothing on this page is computed here.
        </p>
      </div>

      {/* ── Platform view ─────────────────────────────────────────────── */}
      <section className="border rounded bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Platform</h3>
        {!platform && !error && <p className="text-sm text-gray-500">Loading…</p>}
        {platform && (
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Last walkthrough</div>
              {platform.walkthrough ? (
                <>
                  <div className="mt-1 flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      platform.walkthrough.verdict === 'PASS'
                        ? 'bg-green-50 text-green-800 border border-green-200'
                        : 'bg-red-50 text-red-800 border border-red-200'
                    }`}>
                      {platform.walkthrough.verdict}
                    </span>
                    <span className="text-sm text-gray-700">
                      {platform.walkthrough.ageHours ?? '?'} h ago
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {platform.walkthrough.observations?.assertionsPassed ?? 0} passed /{' '}
                    {platform.walkthrough.observations?.assertionsFailed ?? 0} failed ·{' '}
                    {platform.walkthrough.ranAt}
                  </div>
                  {/* §6.1 in one line, so nobody reads the scheduler console instead. */}
                  <p className="text-[11px] text-gray-400 mt-1 leading-snug">
                    {platform.walkthrough.source}
                  </p>
                </>
              ) : (
                <p className="text-sm text-slate-700">
                  No walkthrough record at all — which is not the same as a passing run.
                </p>
              )}
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Open alerts</div>
              {platform.openAlerts.length === 0 ? (
                <p className="text-sm text-gray-700 mt-1">None.</p>
              ) : (
                <ul className="mt-1 space-y-1">
                  {platform.openAlerts.map((a) => (
                    <li key={a.id} className="text-sm text-gray-800">
                      <span className="font-medium">{a.checkId}</span>{' '}
                      <span className="text-gray-500">on {a.projectId}</span>
                      {a.offendingItems[0] && (
                        <div className="text-xs text-gray-600">— {a.offendingItems[0]}</div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </section>

      {/* ── Platform errors (P1 step 9) ───────────────────────────────────
          Sits beside the platform verification state rather than on a page of
          its own: both answer "what is wrong with the platform right now", and
          both are judged the same way. */}
      <PlatformErrorsSection />

      {/* ── Per-study chain ───────────────────────────────────────────── */}
      <section className="border rounded bg-white p-4">
        <form
          className="flex gap-2 items-center"
          onSubmit={(e) => { e.preventDefault(); load(pending); }}
        >
          <input
            value={pending}
            onChange={(e) => setPending(e.target.value)}
            placeholder="Study (project) id"
            className="flex-1 border rounded px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-1 px-3 py-2 rounded bg-blue-600 text-white text-sm disabled:opacity-50"
          >
            <Search className="w-4 h-4" />
            {loading ? 'Reading…' : 'Read chain'}
          </button>
        </form>

        {error && (
          <p className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </p>
        )}

        {view && (
          <div className="mt-4 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-gray-700">
                <strong>{view.projectId}</strong> · {view.recordCount} record(s)
              </span>
              {counts?.map(([label, n]) => (
                <span key={label} className="text-xs text-gray-600">
                  {label}: <strong>{n}</strong>
                </span>
              ))}
            </div>

            {/* The claim, stated only when the records support it. */}
            <div className={`rounded border px-3 py-2 text-sm ${
              view.summary.claimHolds
                ? 'bg-green-50 border-green-200 text-green-900'
                : 'bg-slate-50 border-slate-200 text-slate-800'
            }`}>
              {view.summary.claimHolds ? (
                <>Every link holds in both directions for this study.</>
              ) : (
                <>
                  <strong>The chain claim cannot be made in full for this study.</strong>{' '}
                  Not because of an average — because of these:{' '}
                  {view.summary.blockedBy.slice(0, 8).join('; ')}
                  {view.summary.blockedBy.length > 8 &&
                    ` (and ${view.summary.blockedBy.length - 8} more)`}
                </>
              )}
            </div>

            {view.links.map((l) => (
              <div key={l.id} className="border rounded">
                <div className="px-3 py-2 bg-gray-50 border-b">
                  <span className="font-semibold text-sm text-gray-900">{l.id}</span>
                  <span className="text-xs text-gray-600 ml-2">{l.from} → {l.to}</span>
                </div>
                <div className="grid gap-3 md:grid-cols-2 p-3">
                  <DirectionCell d={l.directions.loss} title="Loss (forward completeness)" />
                  <DirectionCell d={l.directions.invention} title="Invention (backward provenance)" />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
